import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Megaphone, Plus, Trash, PencilSimple, PaperPlaneTilt,
  Clock, MapPin, SpeakerHigh, Bell, Warning, Info,
  CheckCircle, X, Monitor
} from '@phosphor-icons/react';

const TYPES = [
  { value: 'info', label: 'Information', color: '#3B82F6', icon: Info },
  { value: 'urgent', label: 'Urgent', color: '#EF4444', icon: Warning },
  { value: 'reminder', label: 'Reminder', color: '#F59E0B', icon: Clock },
  { value: 'success', label: 'Good News', color: '#22C55E', icon: CheckCircle },
];

const DEFAULT_ROOMS = ['Living Room', 'Kitchen', 'Bedroom', 'Hall', 'Office'];

export default function AnnouncementsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', message: '', type: 'info', priority: 'normal',
    target_rooms: ['all'], scheduled_at: '', expires_at: '',
    tts_enabled: false, notify_channels: []
  });

  const fetchData = async () => {
    try {
      const res = await api.get('/announcements');
      setAnnouncements(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const resetForm = () => {
    setForm({
      title: '', message: '', type: 'info', priority: 'normal',
      target_rooms: ['all'], scheduled_at: '', expires_at: '',
      tts_enabled: false, notify_channels: []
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    try {
      if (editing) {
        await api.put(`/announcements/${editing}`, form);
      } else {
        await api.post('/announcements', form);
      }
      resetForm();
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (ann) => {
    setForm({
      title: ann.title, message: ann.message, type: ann.type, priority: ann.priority,
      target_rooms: ann.target_rooms || ['all'], scheduled_at: ann.scheduled_at || '',
      expires_at: ann.expires_at || '', tts_enabled: ann.tts_enabled || false,
      notify_channels: ann.notify_channels || []
    });
    setEditing(ann.announcement_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/announcements/${id}`);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleBroadcast = async (id) => {
    try {
      await api.post(`/announcements/${id}/broadcast`);
      alert('Broadcast sent!');
    } catch (e) { console.error(e); }
  };

  const toggleRoom = (room) => {
    setForm(prev => {
      const rooms = prev.target_rooms.filter(r => r !== 'all');
      if (room === 'all') return { ...prev, target_rooms: ['all'] };
      const updated = rooms.includes(room) ? rooms.filter(r => r !== room) : [...rooms, room];
      return { ...prev, target_rooms: updated.length ? updated : ['all'] };
    });
  };

  const toggleChannel = (ch) => {
    setForm(prev => ({
      ...prev,
      notify_channels: prev.notify_channels.includes(ch)
        ? prev.notify_channels.filter(c => c !== ch)
        : [...prev.notify_channels, ch]
    }));
  };

  const getTypeInfo = (type) => TYPES.find(t => t.value === type) || TYPES[0];

  const displayUrl = `${window.location.origin}/display`;

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="announcements-page" className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Home Broadcast</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Announcements</h1>
          <p className="text-sm text-neutral-500 mt-1">Broadcast messages to TV displays, speakers & notifications</p>
        </div>
        <div className="flex gap-2">
          <a href="/display" target="_blank" data-testid="open-display-btn"
            className="px-4 py-2.5 rounded-full border border-white/10 text-neutral-400 hover:text-white text-sm font-medium flex items-center gap-2 transition-colors">
            <Monitor size={16} /> TV Display
          </a>
          <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-announcement-btn"
            className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Announcement
          </button>
        </div>
      </div>

      {/* Display URL Info */}
      <div className="glass-card p-4 border-l-2 border-[#00E5FF]/40">
        <div className="flex items-center gap-3">
          <Monitor size={20} className="text-[#00E5FF]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">TV Display URL</p>
            <p className="text-xs text-neutral-500 truncate">Open on your Smart TV browser: <span className="text-[#00E5FF] font-mono">{displayUrl}?room=Living+Room</span></p>
          </div>
          <button onClick={() => navigator.clipboard.writeText(displayUrl)} data-testid="copy-display-url"
            className="px-3 py-1.5 rounded-lg text-xs border border-white/10 text-neutral-400 hover:text-white transition-colors">
            Copy
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="glass-card p-6 animate-fade-up" data-testid="announcement-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-semibold text-lg">{editing ? 'Edit' : 'New'} Announcement</h3>
            <button onClick={resetForm} className="p-1 text-neutral-500 hover:text-white"><X size={18} /></button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="overline block mb-2">Title</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                data-testid="ann-title-input" placeholder="e.g., Dinner is ready!"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
            </div>

            <div>
              <label className="overline block mb-2">Message</label>
              <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                data-testid="ann-message-input" rows={3} placeholder="Details of the announcement..."
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Type</label>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map(tp => (
                    <button key={tp.value} onClick={() => setForm(p => ({ ...p, type: tp.value }))}
                      data-testid={`ann-type-${tp.value}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        form.type === tp.value ? 'border-current' : 'border-white/5 text-neutral-500'
                      }`} style={form.type === tp.value ? { color: tp.color, borderColor: tp.color, backgroundColor: `${tp.color}15` } : {}}>
                      {tp.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Priority</label>
                <div className="flex gap-2">
                  {['normal', 'high', 'critical'].map(p => (
                    <button key={p} onClick={() => setForm(prev => ({ ...prev, priority: p }))}
                      data-testid={`ann-priority-${p}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-all ${
                        form.priority === p ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'border-white/5 text-neutral-500'
                      }`}>{p}</button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Target Rooms</label>
              <div className="flex flex-wrap gap-2">
                {['all', ...DEFAULT_ROOMS].map(room => (
                  <button key={room} onClick={() => toggleRoom(room)}
                    data-testid={`ann-room-${room.replace(/\s/g, '-').toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      (form.target_rooms.includes(room) || (room === 'all' && form.target_rooms.includes('all')))
                        ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]'
                        : 'border-white/5 text-neutral-500'
                    }`}>
                    <MapPin size={10} className="inline mr-1" />{room === 'all' ? 'All Rooms' : room}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Schedule (optional)</label>
                <input type="datetime-local" value={form.scheduled_at} data-testid="ann-schedule-input"
                  onChange={e => setForm(p => ({ ...p, scheduled_at: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">Expires (optional)</label>
                <input type="datetime-local" value={form.expires_at} data-testid="ann-expires-input"
                  onChange={e => setForm(p => ({ ...p, expires_at: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.tts_enabled} data-testid="ann-tts-toggle"
                  onChange={e => setForm(p => ({ ...p, tts_enabled: e.target.checked }))}
                  className="w-4 h-4 rounded accent-[#FF6B35]" />
                <SpeakerHigh size={14} className="text-[#FF6B35]" />
                <span className="text-sm">Speak aloud (TTS)</span>
              </label>
            </div>

            <div>
              <label className="overline block mb-2">Also Notify Via</label>
              <div className="flex flex-wrap gap-2">
                {['in_app', 'push', 'whatsapp', 'telegram', 'email'].map(ch => (
                  <button key={ch} onClick={() => toggleChannel(ch)}
                    data-testid={`ann-channel-${ch}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-all ${
                      form.notify_channels.includes(ch)
                        ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]'
                        : 'border-white/5 text-neutral-500'
                    }`}>{ch.replace('_', ' ')}</button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSubmit} data-testid="ann-submit-btn"
                className="px-6 py-2.5 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2">
                <PaperPlaneTilt size={16} /> {editing ? 'Update' : 'Broadcast'}
              </button>
              <button onClick={resetForm} className="px-4 py-2 rounded-full border border-white/10 text-neutral-400 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements List */}
      <div className="space-y-3">
        {announcements.length === 0 ? (
          <div className="glass-card p-10 text-center">
            <Megaphone size={40} weight="duotone" className="text-neutral-600 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm">No announcements yet. Create one to broadcast to your home displays.</p>
          </div>
        ) : announcements.map(ann => {
          const typeInfo = getTypeInfo(ann.type);
          const TypeIcon = typeInfo.icon;
          return (
            <div key={ann.announcement_id} data-testid={`announcement-${ann.announcement_id}`}
              className="glass-card p-5 border-l-2 animate-fade-up" style={{ borderLeftColor: typeInfo.color }}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${typeInfo.color}15` }}>
                    <TypeIcon size={18} style={{ color: typeInfo.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-white">{ann.title}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: `${typeInfo.color}15`, color: typeInfo.color }}>
                        {typeInfo.label}
                      </span>
                      {ann.priority !== 'normal' && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          ann.priority === 'critical' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'
                        }`}>{ann.priority}</span>
                      )}
                      {ann.tts_enabled && <SpeakerHigh size={12} className="text-[#FF6B35]" />}
                    </div>
                    {ann.message && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{ann.message}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-neutral-500">
                      <span>{new Date(ann.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="flex items-center gap-1"><MapPin size={10} />{(ann.target_rooms || ['all']).join(', ')}</span>
                      {ann.created_by && <span>by {ann.created_by}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => handleBroadcast(ann.announcement_id)} data-testid={`broadcast-${ann.announcement_id}`}
                    title="Re-broadcast"
                    className="p-2 rounded-lg text-neutral-500 hover:text-[#FF6B35] hover:bg-white/5 transition-colors">
                    <Megaphone size={14} />
                  </button>
                  <button onClick={() => handleEdit(ann)} className="p-2 rounded-lg text-neutral-500 hover:text-white hover:bg-white/5 transition-colors">
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => handleDelete(ann.announcement_id)} className="p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-white/5 transition-colors">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
